import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  inputClassName,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import {
  AREA_LABELS,
  MEMBERSHIP_AREAS,
  formatAreas,
  type MembershipArea,
} from "../../lib/areas";
import { meQueryOptions } from "../../lib/auth";
import { formatDate } from "../../lib/format";
import type {
  ReplyTemplate,
  ReplyTemplateSurface,
} from "../../lib/reply-templates";

type SettingsSearch = { tab?: "profile" | "team" | "organization" };

export const Route = createFileRoute("/_app/settings")({
  validateSearch: (search: Record<string, unknown>): SettingsSearch => ({
    tab:
      search.tab === "team" || search.tab === "organization"
        ? search.tab
        : undefined,
  }),
  component: SettingsPage,
});

const ROLE_LABEL: Record<string, string> = {
  owner: "Dono",
  admin: "Admin",
  member: "Membro",
};

function apiErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object" && "value" in error) {
    const value = (error as { value: unknown }).value;
    if (
      value &&
      typeof value === "object" &&
      "error" in value &&
      typeof (value as { error: unknown }).error === "string"
    ) {
      return (value as { error: string }).error;
    }
  }
  return fallback;
}

function SettingsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const me = useQuery(meQueryOptions);
  const team = useQuery({
    queryKey: ["team"],
    queryFn: async () => unwrap(await api.team.get()),
  });

  const isOwner = team.data?.role === "owner" || me.data?.role === "owner";
  const tab = search.tab ?? "profile";

  return (
    <>
      <PageHeader
        title="Configurações"
        description="Seu perfil, a equipe e a organização."
      />

      <Tabs
        value={tab}
        onValueChange={(value) =>
          navigate({
            search: {
              tab: value === "profile" ? undefined : (value as SettingsSearch["tab"]),
            },
            replace: true,
          })
        }
      >
        <TabsList className="mb-4 w-full">
          <TabsTab value="profile">Perfil</TabsTab>
          <TabsTab value="team">Equipe</TabsTab>
          {isOwner && <TabsTab value="organization">Organização</TabsTab>}
        </TabsList>

        <TabsPanel value="profile" className="outline-none">
          <ProfilePanel />
        </TabsPanel>
        <TabsPanel value="team" className="outline-none">
          <TeamPanel isOwner={isOwner} />
        </TabsPanel>
        {isOwner && (
          <TabsPanel value="organization" className="outline-none">
            <OrganizationPanel />
          </TabsPanel>
        )}
      </Tabs>
    </>
  );
}

function ProfilePanel() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const me = useQuery(meQueryOptions);

  const [name, setName] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const displayName = name ?? me.data?.user?.name ?? "";
  const impersonated = me.data && "impersonated" in me.data && me.data.impersonated;

  const saveProfile = useMutation({
    mutationFn: async () =>
      unwrap(await api.auth.profile.patch({ name: displayName.trim() })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.add({ type: "success", title: "Perfil atualizado" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível salvar",
        description: apiErrorMessage(error, "Tente de novo em instantes."),
      });
    },
  });

  const changePassword = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.auth["change-password"].post({
          currentPassword,
          newPassword,
        }),
      ),
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      toast.add({ type: "success", title: "Senha atualizada" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível trocar a senha",
        description: apiErrorMessage(error, "Confira a senha atual."),
      });
    },
  });

  return (
    <div className="flex flex-col gap-3">
      <Card className="animate-rise">
        <CardHeader>
          <CardTitle>Seus dados</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="flex max-w-sm flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              saveProfile.mutate();
            }}
          >
            <Field>
              <FieldLabel>Nome</FieldLabel>
              <Input
                required
                minLength={2}
                value={displayName}
                onChange={(event) => setName(event.target.value)}
                disabled={Boolean(impersonated)}
              />
            </Field>
            <Field>
              <FieldLabel>E-mail</FieldLabel>
              <Input value={me.data?.user?.email ?? ""} disabled />
              <p className="mt-1 text-2xs text-ink-muted">
                Para trocar o e-mail de acesso, fale com o suporte.
              </p>
            </Field>
            <Button
              type="submit"
              className="self-start"
              loading={saveProfile.isPending}
              disabled={Boolean(impersonated)}
            >
              Salvar
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="animate-rise [animation-delay:40ms]">
        <CardHeader>
          <CardTitle>Sua senha</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="flex max-w-sm flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              changePassword.mutate();
            }}
          >
            <Field>
              <FieldLabel>Senha atual</FieldLabel>
              <Input
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel>Nova senha</FieldLabel>
              <Input
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                placeholder="Mínimo 8 caracteres"
              />
            </Field>
            <Button
              type="submit"
              className="self-start"
              loading={changePassword.isPending}
            >
              Atualizar senha
            </Button>
          </form>
        </CardContent>
      </Card>

      <SecurityPanel impersonated={Boolean(impersonated)} />
    </div>
  );
}

function describeAgent(userAgent: string | null) {
  if (!userAgent) return "Navegador";
  if (userAgent.includes("Edg/")) return "Edge";
  if (userAgent.includes("Chrome/")) return "Chrome";
  if (userAgent.includes("Firefox/")) return "Firefox";
  if (userAgent.includes("Safari/")) return "Safari";
  return userAgent.slice(0, 40);
}

function SecurityPanel({ impersonated }: { impersonated: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const twoFactor = useQuery({
    queryKey: ["auth", "2fa"],
    queryFn: async () => unwrap(await api.auth["2fa"].get()),
  });
  const sessions = useQuery({
    queryKey: ["auth", "sessions"],
    queryFn: async () => unwrap(await api.auth.sessions.get()),
  });

  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(
    null,
  );
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const startSetup = useMutation({
    mutationFn: async () => unwrap(await api.auth["2fa"].setup.post()),
    onSuccess: (data) => {
      setSetup(data);
      setBackupCodes(null);
      setCode("");
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível iniciar o 2FA",
        description: apiErrorMessage(error, "Tente de novo."),
      });
    },
  });

  const confirm = useMutation({
    mutationFn: async () =>
      unwrap(await api.auth["2fa"].confirm.post({ code: code.trim() })),
    onSuccess: (data) => {
      setSetup(null);
      setCode("");
      setBackupCodes(data.backupCodes);
      void queryClient.invalidateQueries({ queryKey: ["auth", "2fa"] });
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.add({ type: "success", title: "Verificação em dois passos ativa" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Código inválido",
        description: apiErrorMessage(error, "Confira o app autenticador."),
      });
    },
  });

  const disable = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.auth["2fa"].disable.post({
          password: disablePassword,
          code: disableCode.trim(),
        }),
      ),
    onSuccess: () => {
      setDisablePassword("");
      setDisableCode("");
      setBackupCodes(null);
      void queryClient.invalidateQueries({ queryKey: ["auth", "2fa"] });
      toast.add({ type: "success", title: "2FA desativado" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível desativar",
        description: apiErrorMessage(error, "Confira a senha e o código."),
      });
    },
  });

  const revokeOthers = useMutation({
    mutationFn: async () =>
      unwrap(await api.auth.sessions["revoke-others"].post()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["auth", "sessions"] });
      toast.add({ type: "success", title: "Outras sessões encerradas" });
    },
  });

  const revoke = useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.auth.sessions({ id }).delete()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["auth", "sessions"] });
    },
  });

  return (
    <>
      <Card className="animate-rise [animation-delay:80ms]">
        <CardHeader>
          <CardTitle>Verificação em dois passos</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-2xs text-ink-muted">
            O Bridge altera estoque e preço nas lojas. Um código no celular
            protege a conta se a senha vazar.
          </p>
          {twoFactor.data?.enabled && !setup ? (
            <form
              className="flex max-w-sm flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                disable.mutate();
              }}
            >
              <p className="text-[0.8125rem] font-medium text-ink">Ativa</p>
              <Field>
                <FieldLabel>Senha</FieldLabel>
                <Input
                  type="password"
                  required
                  value={disablePassword}
                  onChange={(event) => setDisablePassword(event.target.value)}
                  disabled={impersonated}
                />
              </Field>
              <Field>
                <FieldLabel>Código do app ou reserva</FieldLabel>
                <Input
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={disableCode}
                  onChange={(event) => setDisableCode(event.target.value)}
                  disabled={impersonated}
                />
              </Field>
              <Button
                type="submit"
                variant="ghost"
                className="self-start"
                loading={disable.isPending}
                disabled={impersonated}
              >
                Desativar
              </Button>
            </form>
          ) : setup ? (
            <form
              className="flex max-w-sm flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                confirm.mutate();
              }}
            >
              <p className="text-2xs text-ink-muted">
                Adicione esta chave no autenticador (Google Authenticator,
                1Password, Authy) e confirme com o código de 6 dígitos.
              </p>
              <code className="break-all rounded-control bg-surface-sunken px-2 py-1.5 font-mono text-2xs text-ink">
                {setup.secret}
              </code>
              <Field>
                <FieldLabel>Código</FieldLabel>
                <Input
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </Field>
              <Button type="submit" className="self-start" loading={confirm.isPending}>
                Confirmar
              </Button>
            </form>
          ) : (
            <Button
              className="self-start"
              loading={startSetup.isPending}
              disabled={impersonated}
              onClick={() => startSetup.mutate()}
            >
              Ativar 2FA
            </Button>
          )}
          {backupCodes && (
            <div className="rounded-control border border-line bg-surface-sunken px-3 py-2">
              <p className="text-2xs font-medium text-ink">
                Guarde estes códigos. Cada um vale uma vez.
              </p>
              <ul className="mt-2 grid grid-cols-2 gap-1 font-mono text-2xs text-ink">
                {backupCodes.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="animate-rise [animation-delay:120ms]">
        <CardHeader>
          <CardTitle>Sessões ativas</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {sessions.isLoading ? (
            <Spinner className="size-5 text-ink-muted" />
          ) : (
            <ul className="flex flex-col gap-2">
              {sessions.data?.sessions.map((session) => (
                <li
                  key={session.id}
                  className="flex items-center justify-between gap-3 rounded-control border border-line px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-[0.8125rem] font-medium text-ink">
                      {describeAgent(session.userAgent)}
                      {session.current ? " · este dispositivo" : ""}
                      {session.support ? " · suporte" : ""}
                    </p>
                    <p className="text-2xs text-ink-muted">
                      {session.ip ?? "IP desconhecido"} · desde{" "}
                      {formatDate(session.createdAt)}
                    </p>
                  </div>
                  {!session.current && !impersonated && (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={revoke.isPending}
                      onClick={() => revoke.mutate(session.id)}
                    >
                      Encerrar
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <Button
            variant="ghost"
            className="self-start"
            loading={revokeOthers.isPending}
            disabled={impersonated}
            onClick={() => revokeOthers.mutate()}
          >
            Encerrar as outras sessões
          </Button>
        </CardContent>
      </Card>
    </>
  );
}

function AreaChecks({
  value,
  onChange,
}: {
  value: MembershipArea[];
  onChange: (next: MembershipArea[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-3">
      {MEMBERSHIP_AREAS.map((area) => {
        const checked = value.includes(area);
        return (
          <label
            key={area}
            className="flex items-center gap-2 text-2xs text-ink-secondary"
          >
            <input
              type="checkbox"
              className="size-3.5 rounded-sm border-line accent-ember"
              checked={checked}
              onChange={() =>
                onChange(
                  checked ? value.filter((item) => item !== area) : [...value, area],
                )
              }
            />
            {AREA_LABELS[area]}
          </label>
        );
      })}
    </div>
  );
}

function TeamPanel({ isOwner }: { isOwner: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const me = useQuery(meQueryOptions);
  const team = useQuery({
    queryKey: ["team"],
    queryFn: async () => unwrap(await api.team.get()),
  });

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "admin">("member");
  const [inviteAreas, setInviteAreas] = useState<MembershipArea[]>([]);

  const invite = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.team.invites.post({
          email: email.trim(),
          role,
          areas: role === "member" && inviteAreas.length > 0 ? inviteAreas : undefined,
        }),
      ),
    onSuccess: () => {
      setEmail("");
      setInviteAreas([]);
      void queryClient.invalidateQueries({ queryKey: ["team"] });
      toast.add({
        type: "success",
        title: "Convite enviado",
        description: "A pessoa recebe um e-mail com o link para entrar.",
      });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível convidar",
        description: apiErrorMessage(error, "Tente de novo em instantes."),
      });
    },
  });

  const revoke = useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.team.invites({ id }).delete()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["team"] });
      toast.add({ type: "success", title: "Convite cancelado" });
    },
  });

  const updateAreas = useMutation({
    mutationFn: async (input: { userId: string; areas: MembershipArea[] }) =>
      unwrap(
        await api.team.members({ userId: input.userId }).patch({
          areas: input.areas.length > 0 ? input.areas : null,
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["team"] });
      toast.add({ type: "success", title: "Áreas atualizadas" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível atualizar as áreas",
        description: apiErrorMessage(error, "Tente de novo."),
      });
    },
  });

  const remove = useMutation({
    mutationFn: async (userId: string) =>
      unwrap(await api.team.members({ userId }).delete()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["team"] });
      toast.add({ type: "success", title: "Membro removido" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível remover",
        description: apiErrorMessage(error, "Tente de novo."),
      });
    },
  });

  return (
    <Card className="animate-rise">
      <CardHeader>
        <CardTitle>Pessoas</CardTitle>
      </CardHeader>
      {team.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner className="size-5 text-ink-muted" />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Nome</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Papel</TableHead>
              <TableHead>Áreas</TableHead>
              {isOwner && <TableHead className="text-right"> </TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {team.data?.members.map((member) => (
              <TableRow key={member.id}>
                <TableCell className="font-medium text-ink">
                  {member.name}
                </TableCell>
                <TableCell className="font-mono text-2xs text-ink-muted">
                  {member.email}
                </TableCell>
                <TableCell>{ROLE_LABEL[member.role] ?? member.role}</TableCell>
                <TableCell className="text-2xs text-ink-muted">
                  {member.role === "member" && isOwner ? (
                    <AreaChecks
                      value={(member.areas ?? []) as MembershipArea[]}
                      onChange={(areas) =>
                        updateAreas.mutate({ userId: member.userId, areas })
                      }
                    />
                  ) : (
                    formatAreas(
                      member.role === "member" ? member.areas : null,
                    )
                  )}
                </TableCell>
                {isOwner && (
                  <TableCell className="text-right">
                    {member.userId !== me.data?.user.id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={remove.isPending}
                        onClick={() => remove.mutate(member.userId)}
                      >
                        Remover
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {team.data?.invites.map((pending) => (
              <TableRow key={pending.id}>
                <TableCell className="text-ink-muted">Convite</TableCell>
                <TableCell className="font-mono text-2xs text-ink-muted">
                  {pending.email}
                </TableCell>
                <TableCell>
                  {ROLE_LABEL[pending.role] ?? pending.role} · até{" "}
                  {formatDate(pending.expiresAt)}
                </TableCell>
                <TableCell className="text-2xs text-ink-muted">
                  {formatAreas(pending.role === "member" ? pending.areas : null)}
                </TableCell>
                {isOwner && (
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={revoke.isPending}
                      onClick={() => revoke.mutate(pending.id)}
                    >
                      Cancelar
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {isOwner && (
        <CardContent className="border-t border-line">
          <form
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
            onSubmit={(event) => {
              event.preventDefault();
              invite.mutate();
            }}
          >
            <Field className="sm:flex-1">
              <FieldLabel>E-mail</FieldLabel>
              <Input
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="pessoa@loja.com"
              />
            </Field>
            <Field className="sm:w-40">
              <FieldLabel>Papel</FieldLabel>
              <Select
                value={role}
                itemToStringLabel={(value) =>
                  ROLE_LABEL[value as string] ?? String(value)
                }
                onValueChange={(value) => setRole(value as "member" | "admin")}
              >
                <SelectTrigger />
                <SelectContent>
                  <SelectItem value="member">Membro</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Button type="submit" loading={invite.isPending}>
              Enviar convite
            </Button>
          </form>
          {role === "member" && (
            <div className="mt-3">
              <p className="mb-1.5 text-2xs text-ink-muted">
                Áreas (vazio = acesso atual, sem restrição)
              </p>
              <AreaChecks value={inviteAreas} onChange={setInviteAreas} />
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

type NotificationKey =
  | "questions"
  | "messages"
  | "claims"
  | "stock"
  | "replenish"
  | "orders"
  | "reputation"
  | "conversion"
  | "buybox"
  | "notice"
  | "accountant"
  | "weekly";

type NotificationPrefs = Record<NotificationKey, boolean>;

const DEFAULT_NOTIFICATIONS: NotificationPrefs = {
  questions: true,
  messages: true,
  claims: true,
  stock: true,
  replenish: true,
  orders: true,
  reputation: true,
  conversion: true,
  buybox: true,
  notice: true,
  accountant: true,
  weekly: true,
};

const NOTIFICATION_ROWS: {
  key: NotificationKey;
  title: string;
  description: string;
}[] = [
  {
    key: "questions",
    title: "Perguntas sem resposta",
    description: "Quando o Mercado Livre cobra tempo de resposta.",
  },
  {
    key: "messages",
    title: "Mensagens de comprador",
    description: "Pós-venda ainda sem resposta na caixa.",
  },
  {
    key: "claims",
    title: "Reclamações e devoluções",
    description: "Prazo vencendo ou pendente de ação.",
  },
  {
    key: "stock",
    title: "Estoque zerado",
    description: "SKU vinculado a anúncio sem unidades.",
  },
  {
    key: "replenish",
    title: "Reposição",
    description: "Sugestão de compra quando a cobertura cai abaixo do prazo.",
  },
  {
    key: "orders",
    title: "Pedidos com problema",
    description: "Inválidos ou não entregues — cancelamento de comprador fica de fora.",
  },
  {
    key: "reputation",
    title: "Reputação",
    description:
      "Quando reclamações, atraso de despacho ou cancelamentos do vendedor se aproximam do amarelo do ML.",
  },
  {
    key: "conversion",
    title: "Queda de conversão",
    description:
      "Quando um anúncio com tráfego perde metade da conversão em 30 dias.",
  },
  {
    key: "buybox",
    title: "Card do catálogo",
    description:
      "Quando um anúncio deixa de ganhar ou dividir o card do produto.",
  },
  {
    key: "notice",
    title: "Comunicados do Mercado Livre",
    description:
      "Alertas oficiais da conta — mudança de política, pendência ou bloqueio.",
  },
  {
    key: "accountant",
    title: "Pacote do contador",
    description:
      "Zip mensal no e-mail dos donos (NF-e, extrato e lucro) no fechamento do mês.",
  },
  {
    key: "weekly",
    title: "Resumo semanal",
    description:
      "Vendas, lucro, reputação e top anúncios no e-mail, toda segunda.",
  },
];

function OrganizationPanel() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const me = useQuery(meQueryOptions);
  const team = useQuery({
    queryKey: ["team"],
    queryFn: async () => unwrap(await api.team.get()),
  });
  const [name, setName] = useState<string | null>(null);
  const [prefsDraft, setPrefsDraft] = useState<NotificationPrefs | null>(null);

  const displayName = name ?? me.data?.tenant?.name ?? "";
  const prefs =
    prefsDraft ??
    (team.data && "notifications" in team.data
      ? (team.data.notifications as NotificationPrefs)
      : DEFAULT_NOTIFICATIONS);
  const anyOperationalOn = Object.values(prefs).some(Boolean);

  const rename = useMutation({
    mutationFn: async () =>
      unwrap(await api.team.organization.patch({ name: displayName.trim() })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.add({ type: "success", title: "Organização atualizada" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível salvar",
        description: apiErrorMessage(error, "Tente de novo em instantes."),
      });
    },
  });

  const savePrefs = useMutation({
    mutationFn: async (notifications: NotificationPrefs) =>
      unwrap(await api.team.organization.patch({ notifications })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["team"] });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível atualizar as notificações",
        description: apiErrorMessage(error, "Tente de novo em instantes."),
      });
    },
  });

  function togglePref(key: NotificationKey) {
    const previous = prefs;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefsDraft(next);
    savePrefs.mutate(next, { onError: () => setPrefsDraft(previous) });
  }

  return (
    <div className="flex flex-col gap-4 animate-rise">
      <Card>
        <CardHeader>
          <CardTitle>Organização</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="flex max-w-sm flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              rename.mutate();
            }}
          >
            <Field>
              <FieldLabel>Nome da empresa</FieldLabel>
              <Input
                required
                minLength={2}
                value={displayName}
                onChange={(event) => setName(event.target.value)}
              />
              <p className="mt-1 text-2xs text-ink-muted">
                Aparece no menu lateral e nos e-mails da equipe.
              </p>
            </Field>
            <Button
              type="submit"
              className="self-start"
              loading={rename.isPending}
            >
              Salvar
            </Button>
          </form>
          <div className="mt-5 border-t border-line pt-4">
            <p className="text-[0.8125rem] font-medium text-ink">
              Exportar dados
            </p>
            <p className="mt-0.5 text-2xs text-ink-muted">
              Zip com pedidos, anúncios, estoque, mensagens e o resto da
              organização. Sem senhas nem tokens das lojas.
            </p>
            <Button
              type="button"
              variant="ghost"
              className="mt-2"
              onClick={async () => {
                try {
                  const response = await fetch(
                    `${import.meta.env.VITE_API_URL ?? "http://localhost:3000"}/export`,
                    { credentials: "include" },
                  );
                  if (!response.ok) throw new Error("export failed");
                  const blob = await response.blob();
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = "forge-dados.zip";
                  link.click();
                  URL.revokeObjectURL(url);
                } catch (error) {
                  toast.add({
                    type: "error",
                    title: "Não foi possível exportar",
                    description: apiErrorMessage(error, "Tente de novo."),
                  });
                }
              }}
            >
              Baixar zip
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notificações</CardTitle>
        </CardHeader>
        <ul className="divide-y divide-line">
          {NOTIFICATION_ROWS.map((row) => (
            <li key={row.key}>
              <label className="flex cursor-pointer items-start justify-between gap-4 px-4 py-3 transition-colors duration-150 ease-swift [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken">
                <span className="min-w-0">
                  <span className="block text-[0.8125rem] font-medium text-ink">
                    {row.title}
                  </span>
                  <span className="mt-0.5 block text-2xs text-ink-muted">
                    {row.description}
                  </span>
                </span>
                <Switch
                  className="mt-0.5"
                  checked={prefs[row.key]}
                  onCheckedChange={() => togglePref(row.key)}
                />
              </label>
            </li>
          ))}
        </ul>
        <p className="border-t border-line px-4 py-3 text-2xs text-ink-muted">
          {anyOperationalOn
            ? "Alertas operacionais vão num digest. O pacote do contador é um e-mail à parte no fechamento do mês. Cobrança e trial continuam sempre ativos."
            : "Nenhum alerta operacional será enviado. Cobrança e trial continuam sempre ativos."}
        </p>
      </Card>

      <ReplyTemplatesCard
        templates={
          team.data && "replyTemplates" in team.data
            ? team.data.replyTemplates
            : []
        }
        onError={(message) =>
          toast.add({
            type: "error",
            title: "Não foi possível salvar os atalhos",
            description: message,
          })
        }
      />
    </div>
  );
}

const SURFACE_LABEL: Record<ReplyTemplateSurface, string> = {
  questions: "Perguntas",
  messages: "Mensagens",
  claims: "Reclamações",
};

const BUILTIN_IDS = new Set(["thanks", "tracking", "eta", "checking"]);

function slugTemplateId(title: string) {
  const slug = title
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
  return slug || `tpl-${crypto.randomUUID().slice(0, 8)}`;
}

function ReplyTemplatesCard({
  templates,
  onError,
}: {
  templates: ReplyTemplate[];
  onError: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ReplyTemplate[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [surfaces, setSurfaces] = useState<ReplyTemplateSurface[]>([
    "questions",
    "messages",
    "claims",
  ]);
  const rows = draft ?? templates;

  const save = useMutation({
    mutationFn: async (replyTemplates: ReplyTemplate[]) =>
      unwrap(await api.team.organization.patch({ replyTemplates })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["team"] });
      setDraft(null);
      setAdding(false);
      setTitle("");
      setBody("");
    },
    onError: (error) => {
      onError(
        error instanceof Error ? error.message : "Tente de novo em instantes.",
      );
    },
  });

  function toggleSurface(surface: ReplyTemplateSurface) {
    setSurfaces((current) =>
      current.includes(surface)
        ? current.filter((item) => item !== surface)
        : [...current, surface],
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Respostas rápidas</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-2xs text-ink-muted">
          Atalhos nas caixas de perguntas, mensagens e reclamações. Variáveis:{" "}
          <span className="font-mono">{"{{comprador}}"}</span>,{" "}
          <span className="font-mono">{"{{pedido}}"}</span>,{" "}
          <span className="font-mono">{"{{rastreio}}"}</span>,{" "}
          <span className="font-mono">{"{{prazo}}"}</span>. O clique preenche o
          texto — nunca envia sozinho.
        </p>
        <ul className="divide-y divide-line rounded-control border border-line">
          {rows.map((template) => (
            <li key={template.id} className="flex flex-col gap-1.5 px-3 py-2.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[0.8125rem] font-medium text-ink">
                  {template.title}
                </p>
                <div className="flex flex-wrap items-center justify-end gap-1">
                  {template.surfaces.map((surface) => (
                    <Badge key={surface} variant="neutral">
                      {SURFACE_LABEL[surface]}
                    </Badge>
                  ))}
                  {!BUILTIN_IDS.has(template.id) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const next = rows.filter((row) => row.id !== template.id);
                        setDraft(next);
                        save.mutate(next);
                      }}
                    >
                      Remover
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-2xs text-ink-muted">{template.body}</p>
            </li>
          ))}
        </ul>
        {adding ? (
          <form
            className="flex flex-col gap-3 rounded-control border border-line p-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!title.trim() || !body.trim() || surfaces.length === 0) return;
              const next = [
                ...rows,
                {
                  id: slugTemplateId(title),
                  title: title.trim(),
                  body: body.trim(),
                  surfaces,
                },
              ];
              setDraft(next);
              save.mutate(next);
            }}
          >
            <Field>
              <FieldLabel>Título do atalho</FieldLabel>
              <Input
                required
                maxLength={40}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Horário de atendimento"
              />
            </Field>
            <Field>
              <FieldLabel>Texto</FieldLabel>
              <textarea
                required
                maxLength={1000}
                rows={3}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                className={`${inputClassName} h-auto min-h-20 py-2`}
                placeholder="Atendemos de segunda a sexta, das 8h às 18h."
              />
              <FieldDescription>
                Use as variáveis acima se o atalho depender do pedido.
              </FieldDescription>
            </Field>
            <div className="flex flex-wrap gap-1.5">
              {(
                ["questions", "messages", "claims"] as ReplyTemplateSurface[]
              ).map((surface) => (
                <Button
                  key={surface}
                  type="button"
                  size="sm"
                  variant={surfaces.includes(surface) ? "primary" : "secondary"}
                  onClick={() => toggleSurface(surface)}
                >
                  {SURFACE_LABEL[surface]}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" loading={save.isPending}>
                Adicionar
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setAdding(false)}
              >
                Cancelar
              </Button>
            </div>
          </form>
        ) : (
          <Button
            type="button"
            variant="secondary"
            className="self-start"
            onClick={() => setAdding(true)}
          >
            Novo atalho
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
